/**
 * Sample prose for /?test=doc-comments - adapted from real Speedy caching
 * work spec (RDEK), ASCII-normalized. Demonstrates headings, long
 * paragraphs, tables, lists, and code - the shapes TipTap comment marks
 * must survive.
 *
 * Source doc: hop-studios/clients/RDEK/.../2026-07-22-speedy-caching-work-spec.md
 */

export type ProseBlockKind =
  | 'heading'
  | 'paragraph'
  | 'list'
  | 'code'
  | 'table'
  | 'hr'

export interface ProseBlock {
  id: string
  kind: ProseBlockKind
  /** heading level 1-3 */
  level?: number
  /** For kind:list - render as <ol> when true */
  ordered?: boolean
  /** Display text (markdown-ish plain for lists/tables/code) */
  text: string
}

export const SAMPLE_DOC_META = {
  title: 'Speedy caching work spec - RDEK',
  subtitle: 'Real work-spec body (trimmed) - hop-studios/clients/RDEK',
  sourcePath:
    'hop-studios/clients/RDEK/todos/10113586696-configure-speedy-plan-estimate/2026-07-22-speedy-caching-work-spec.md',
}

/** Block-level nodes TipTap would expose as anchors (or mark ranges inside). */
export const SAMPLE_PROSE_BLOCKS: ProseBlock[] = [
  {
    id: 'h-title',
    kind: 'heading',
    level: 1,
    text: 'Speedy caching work spec - RDEK',
  },
  {
    id: 'p-meta',
    kind: 'paragraph',
    text: 'Date: 2026-07-22 (revised: static-only + granular clears + JS alerts). Todo: Create plan and estimate to configure Speedy. Branch: feature/tj_speedy-implementation.',
  },
  {
    id: 'h-overview',
    kind: 'heading',
    level: 2,
    text: 'Overview',
  },
  {
    id: 'p-overview-1',
    kind: 'paragraph',
    text: 'Implement Speedy on RDEK with full-page static caching only (no fragments / no hybrid). Same shape as Louv for the static driver and refresh settings, but cache-breaking is channel-specific - not Louv\'s blunt all-channels wipe.',
  },
  {
    id: 'p-overview-2',
    kind: 'paragraph',
    text: 'Sitewide alerts are the hard case: baking them into every static page would force a full-tree clear on every alert edit. Instead, load the banner via a small JS fetch from an uncached EE endpoint so alert edits do not wipe the static tree. Server capacity is fine. The win is serving pre-built HTML and skipping EE on cached hits.',
  },
  {
    id: 'h-decisions',
    kind: 'heading',
    level: 2,
    text: 'Decisions (locked)',
  },
  {
    id: 'tbl-decisions',
    kind: 'table',
    text: [
      'Topic | Decision',
      'Mode | Static-only. {exp:speedy:static} on cacheable public pages',
      'Fragments / hybrid | None',
      'Driver | Static filesystem (speedy_static_enabled = yes)',
      'Refresh | speedy_enable_refresh = no - rebuild on next visit',
      'Logged-in | speedy_logged_out_only = yes - editors must not write public static files',
      'Cache-breaking | Granular per channel. Widget Elements = full static clear',
      'Alerts (sitewide banner) | JS-loaded from uncached alerts/banner - not baked into static HTML',
    ].join('\n'),
  },
  {
    id: 'h-channel',
    kind: 'heading',
    level: 2,
    text: 'Channel inventory',
  },
  {
    id: 'p-channel-intro',
    kind: 'paragraph',
    text: 'Source: SQL dump + template audit (2026-07-22). Front-end channels get Speedy roles; empty/legacy channels are ignored.',
  },
  {
    id: 'tbl-channels',
    kind: 'table',
    text: [
      'Channel | ID | Front-end? | Speedy role',
      'General Page | 2 | Yes - ~375 Structure pages | Main win; clear {page_uri}',
      'Homepage Content | 9 | Yes - site/index | Clear root / homepage override',
      'News Entry | 8 | Yes - /news/ | Path clears + home',
      'Alert | 11 | Global banner + /current_advisories/ | JS banner; clear advisories URI only',
      'Widget Elements | 20 | Sidebar/home via relationship | Full static clear on save',
      'Calendar: Events | 14 | /calendar/ | Path + home clears',
    ].join('\n'),
  },
  {
    id: 'h-recycling',
    kind: 'heading',
    level: 3,
    text: 'Recycling note',
  },
  {
    id: 'p-recycling',
    kind: 'paragraph',
    text: 'Live Recycling 101 is a normal Structure general_page that links out to engage.rdek.bc.ca/recycle. The recycling template group is empty; recycling tool channels have 0 entries. Treat as legacy - no Speedy rules.',
  },
  {
    id: 'h-impl',
    kind: 'heading',
    level: 2,
    text: 'Implementation',
  },
  {
    id: 'h-impl-a',
    kind: 'heading',
    level: 3,
    text: 'A. Install & config',
  },
  {
    id: 'code-config',
    kind: 'code',
    text: [
      "$config['speedy_enabled'] = 'yes';",
      "$config['speedy_driver'] = 'static';",
      "$config['speedy_static_enabled'] = 'yes';",
      "$config['speedy_ttl'] = 0; // indefinite; cleared via cache-breaking rules",
      "$config['speedy_enable_refresh'] = 'no';",
      "$config['speedy_logged_out_only'] = 'yes';",
    ].join('\n'),
  },
  {
    id: 'p-ignore',
    kind: 'paragraph',
    text: 'Plus Speedy .htaccess rewrite rules and writable static/. Ignore URLs: results, signup path(s), alerts/banner, sandbox, test, etc.',
  },
  {
    id: 'h-impl-e',
    kind: 'heading',
    level: 3,
    text: 'E. Alerts - JS dynamic banner',
  },
  {
    id: 'list-alerts',
    kind: 'list',
    text: [
      'Placeholder in layout: empty #site-alert-banners container (same id as today for dismiss JS).',
      'Uncached template alerts/banner - move/adapt current _includes/_alert markup. No {exp:speedy:static}; on ignore list.',
      'JS: fetch banner HTML, inject, run existing localStorage dismiss (rdek_dismissed_alerts). Preserve flash-prevention behavior.',
      'Advisories listing (alerts/index) remains statically cached; cleared when any alert is saved.',
    ].join('\n'),
  },
  {
    id: 'h-smoke',
    kind: 'heading',
    level: 2,
    text: 'Staging smoke tests',
  },
  {
    id: 'list-smoke',
    kind: 'list',
    ordered: true,
    text: [
      'Structure page: second hit shows Speedy static (X-Cache-Generator: Speedy or static file on disk).',
      'Save a general_page - that {page_uri} rebuilds; unrelated pages stay cached.',
      'Open/close an alert - banner updates sitewide via JS without wiping the static tree; /current_advisories/ rebuilds; dismiss still works.',
      'Save a widget_elements entry - full static clear.',
      'Logged-in editor does not poison public static files.',
    ].join('\n'),
  },
  {
    id: 'h-cf',
    kind: 'heading',
    level: 2,
    text: 'Cloudflare note',
  },
  {
    id: 'p-cf',
    kind: 'paragraph',
    text: 'Speedy clear is not the same as CF purge. Static plus aggressive CF HTML cache can still serve stale pages. Call out in the client estimate: verify HTML edge behavior with RDEK IT. Alerts JS reduces urgency for CF purge on alert edits, but page content still needs Speedy (and possibly CF) alignment.',
  },
  {
    id: 'h-open',
    kind: 'heading',
    level: 2,
    text: 'Open questions',
  },
  {
    id: 'list-open',
    kind: 'list',
    text: [
      'Homepage static key: url_override="homepage" vs default /?',
      'Exact Structure URI(s) for email signup ignore list?',
      'Cloudflare HTML caching behavior with RDEK IT?',
    ].join('\n'),
  },
]

/** Split a long paragraph into clickable sentence excerpts (prototype only). */
export function splitSentences(text: string): string[] {
  const parts = text.match(/[^.!?]+[.!?]+|[^.!?]+$/g)
  if (!parts || parts.length < 2) return [text]
  return parts.map((s) => s.trim()).filter(Boolean)
}
